import type { ReadResult } from 'api-read-hook';
import type * as React from 'react';
import type { HydraCollection } from 'src/types';
import type { DashboardTicket } from './TicketRow';

import { css, html } from 'react-strict-dom';
import { Text } from 'src/components';

import DashboardSection from './DashboardSection';
import SectionMessage from './SectionMessage';
import TicketList from './TicketList';
import TicketRow from './TicketRow';

type Props = {
  title: string;
  api: ReadResult<HydraCollection<DashboardTicket>>;
  emptyMessage: string;
  renderAction?: (ticket: DashboardTicket) => React.ReactNode;
  renderFooter?: (ticket: DashboardTicket) => React.ReactNode;
};

/**
 * A ticket list which pulls in the next page of results on demand.
 */
export default function TicketsSection({
  title,
  api,
  emptyMessage,
  renderAction,
  renderFooter,
}: Props) {
  const tickets = api.data?.['hydra:member'];
  const nextPagePath = api.data?.['hydra:view']?.['hydra:next'];

  function handleLoadMore() {
    if (!nextPagePath || !tickets) return;
    api.readMore(nextPagePath, (nextPage) => ({
      ...nextPage,
      'hydra:member': [...tickets, ...nextPage['hydra:member']],
    }));
  }

  let body: React.ReactNode;
  if (api.error) {
    body = <SectionMessage>Could not load tickets</SectionMessage>;
  } else if (!tickets) {
    body = <SectionMessage>Loading...</SectionMessage>;
  } else {
    body = (
      <TicketList isEmpty={tickets.length === 0} emptyMessage={emptyMessage}>
        {tickets.map((ticket) => (
          <TicketRow
            key={ticket.id}
            ticket={ticket}
            action={renderAction && renderAction(ticket)}
            footer={renderFooter && renderFooter(ticket)}
          />
        ))}
      </TicketList>
    );
  }

  return (
    <DashboardSection title={title} count={api.data?.['hydra:totalItems']}>
      {body}
      {nextPagePath && (
        <html.span
          style={styles.loadMore}
          onClick={api.loadingMore ? undefined : handleLoadMore}
        >
          <Text typeface="body.medium" color="accent" noSelect>
            {api.loadingMore ? 'Loading...' : 'Load more'}
          </Text>
        </html.span>
      )}
    </DashboardSection>
  );
}

const styles = css.create({
  loadMore: {
    alignSelf: 'flex-start',
    cursor: 'pointer',
  },
});
